<script lang="ts" setup>
import {
  DialogContent,
  DialogOverlay,
  DialogTitle,
  DialogPortal,
  DialogRoot,
  DialogClose,
  VisuallyHidden,
} from 'reka-ui';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  iframe: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
});

const iframeWrapper = ref<HTMLDivElement | null>(null);
const loading = ref(true);
const isModalOpened = ref(false);

const emit = defineEmits(['close']);

const handleClose = () => {
  emit('close');
};

const isMobile = computed(() =>
  useNuxtApp().$viewport.isLessOrEquals('mobileBig'),
);

watchEffect(() => {
  if (props.isVisible) {
    isModalOpened.value = true;
  }
});

watch(isModalOpened, (open) => {
  if (!open) {
    emit('close');
    loading.value = true;
  }
});

watch(
  () => props.isVisible,
  async (isVisible) => {
    if (isVisible) {
      await nextTick();

      const iframeElement = iframeWrapper.value?.querySelector('iframe');

      if (iframeElement) {
        loading.value = true;

        iframeElement.addEventListener(
          'load',
          () => {
            loading.value = false;
          },
          { once: true },
        );
      }
    }
  },
  { immediate: true },
);
</script>

<template>
  <DialogRoot v-model:open="isModalOpened">
    <DialogPortal>
      <DialogOverlay :class="$style.overlay" />
      <DialogContent :aria-describedby="undefined">
        <VisuallyHidden as-child>
          <DialogTitle :class="$style.title">
            {{ props.title }}
          </DialogTitle>
        </VisuallyHidden>
        <div :class="$style.content">
          <div :class="$style.playerWrapper">
            <Loader v-if="loading" center :small="isMobile" />
            <div
              v-show="!loading"
              ref="iframeWrapper"
              :class="$style.iframeWrapper"
              v-html="iframe"
            />
          </div>
        </div>
        <DialogClose :class="$style.close" aria-label="Close">
          <Button view="icon" @click="handleClose">
            <Icon name="my-icon:i-close-white" />
          </Button>
        </DialogClose>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<style lang="scss" module>
.overlay {
  background-color: rgba(19, 0, 2, 0.7);
  position: fixed;
  inset: 0;
  z-index: 10;
  cursor: pointer;
}

.close {
  position: fixed;
  top: 0;
  right: 0;
  width: 88px;
  height: 88px;
  z-index: 15;
}

.content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: var(--color-gray-bg);
  width: 90vw;
  max-width: 1280px;
  max-height: 90vh;
  aspect-ratio: 16 / 9;
  z-index: 15;
}

.content:focus {
  outline: none;
}

.iframeWrapper {
  width: 100%;
  height: 100%;
}

.playerWrapper {
  width: 100%;
  height: 100%;

  :global(iframe) {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }
}
</style>
